import React from "react";
import { Select } from "components";
import QuestionCard from "elements/QuestionCard";
import { useExemptionsContext } from "../../ExemptionsContext";
import moment from "moment";
import ButtonV2 from "elements/ButtonV2";
import { useEventTracker } from "utils/useEventTracker";
import constants from "utils/constants";
import { usePropertyContext } from "../../context";

const TaxFilingHistoryShared = ({
	variant,
}: {
	variant:
		| "first-owner-federal-return"
		| "first-owner-form-ny-return-last-five-years"
		| "second-owner-return-type";
}) => {
	const {
		next,
		flowId,
		specialFieldsValue,
		setSpecialFieldsValue,
		additionalOwnerFirstNameValue,
		additionalOwnerLastNameValue,
	} = useExemptionsContext();
	const { details } = usePropertyContext();

	const [taxReturnStatus, setTaxReturnStatus] = React.useState(
		specialFieldsValue?.income_tax_history?.[0]?.owner2?.tax_return_type ?? ""
	);

	const trackEvent = useEventTracker();

	const handleSubmit = async () => {
		if (flowId === "NEW_YORK_EXEMPTION") {
			if (variant === "first-owner-federal-return") {
				trackEvent({
					eventName: constants.EVENTS.Exemptions_Flow_Tax_Filing_History_Answer,
					data: {
						Answer: specialFieldsValue?.income_tax_history?.[0]?.owner1
							?.federal_tax_return
							? "Yes"
							: "No",
						"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
							? "Yes"
							: "No",
					},
				});
			}

			if (variant === "second-owner-return-type") {
				trackEvent({
					eventName:
						constants.EVENTS.Exemptions_Flow_Tax_Filing_History_2_Answer,
					data: {
						Answer: taxReturnStatus,
						"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
							? "Yes"
							: "No",
					},
				});
			}
		}
		const trackingEventData = {
			variant: variant,
			"Federal Tax Return Answer": specialFieldsValue?.income_tax_history?.[0]
				?.owner1?.federal_tax_return
				? "Yes"
				: "No",
			"Tax Return Status Answer": taxReturnStatus,
		};
		next({ trackingEventData });
	};

	const twoYearsAgo = React.useMemo(
		() => moment().subtract(2, "years").format("YYYY"),
		[]
	);

	const options = [
		{ value: "Federal Only", label: "Federal Only" },
		{ value: "State Only", label: "State Only" },
		{ value: "Federal and State", label: "Both Federal and State" },
		{
			value: "Did not file either",
			label: `Did not file either in ${twoYearsAgo}`,
		},
	];

	if (variant === "first-owner-federal-return") {
		return (
			<form
				onSubmit={e => {
					e.preventDefault();
					handleSubmit();
				}}>
				<QuestionCard
					id="taxFilingHistory"
					value={
						specialFieldsValue?.income_tax_history?.[0]?.owner1
							?.federal_tax_return
					}
					setValue={(v?: boolean) => {
						setSpecialFieldsValue({
							income_tax_history: [
								{
									...specialFieldsValue?.income_tax_history?.[0],
									tax_year: Number(twoYearsAgo),
									owner1: {
										...specialFieldsValue?.income_tax_history?.[0]?.owner1,
										federal_tax_return: v,
									},
								},
							],
						});
					}}
					tooltipText=""
					question={`Did you file a federal tax return in ${twoYearsAgo}?`}></QuestionCard>
				<ButtonV2
					size="large"
					disabled={
						specialFieldsValue?.income_tax_history?.[0]?.owner1
							?.federal_tax_return === undefined
					}
					type="submit">
					Continue
				</ButtonV2>
			</form>
		);
	}

	if (variant === "first-owner-form-ny-return-last-five-years") {
		return (
			<form
				onSubmit={e => {
					e.preventDefault();
					handleSubmit();
				}}>
				<QuestionCard
					id="taxFilingHistory"
					value={
						specialFieldsValue?.filed_ny_state_tax_return_within_last_5_years
					}
					setValue={(v?: boolean) => {
						setSpecialFieldsValue({
							filed_ny_state_tax_return_within_last_5_years: v,
						});
					}}
					tooltipText=""
					question={`Did you file a New York State tax return (form IT-201 or IT-203) for at least one of the past five years?`}></QuestionCard>
				<ButtonV2
					size="large"
					disabled={
						specialFieldsValue?.filed_ny_state_tax_return_within_last_5_years ===
						undefined
					}
					type="submit">
					Continue
				</ButtonV2>
			</form>
		);
	}

	return (
		<form
			onSubmit={e => {
				e.preventDefault();
				handleSubmit();
			}}>
			<div className="property-profile-question-card exemption-survey-card">
				<div className="property-profile-question-card-title-container">
					<p>{`Did ${additionalOwnerFirstNameValue} ${additionalOwnerLastNameValue} file a federal and New York state tax return in ${twoYearsAgo}?`}</p>
				</div>
				<Select
					className="exemptions-card-input"
					required
					value={taxReturnStatus}
					onChange={e => {
						setTaxReturnStatus(e.target.value);
						setSpecialFieldsValue({
							income_tax_history: [
								{
									...specialFieldsValue?.income_tax_history?.[0],
									tax_year: Number(twoYearsAgo),
									owner2: {
										...specialFieldsValue?.income_tax_history?.[0]?.owner2,
										state: "NY",
										tax_return_type: e.target.value,
									},
								},
							],
						});
					}}>
					<option value="" hidden disabled>
						Tax Return Status
					</option>
					{options.map(({ label, value }) => (
						<option key={value} value={value}>
							{label}
						</option>
					))}
				</Select>
			</div>
			<ButtonV2
				size="large"
				disabled={
					!specialFieldsValue?.income_tax_history?.[0]?.owner2?.tax_return_type
				}
				type="submit">
				Continue
			</ButtonV2>
		</form>
	);
};

export const TaxFilingHistory = () => (
	<TaxFilingHistoryShared variant="first-owner-federal-return" />
);
export const TaxFilingHistory2 = () => (
	<TaxFilingHistoryShared variant="second-owner-return-type" />
);
export const TaxFilingHistoryNYLastFiveYears = () => (
	<TaxFilingHistoryShared variant="first-owner-form-ny-return-last-five-years" />
);
